import {query} from "@/graphql/client";
import {
    AInesAssistantType,
    AInesObjective,
    AInesObjectiveState,
    AInesObjectiveSubject,
    Employee
} from "@/graphql/generated/graphql";
import {presentScriptedMessage, sendUserMessage, setUpAssistant} from "./chatBL";
import {defineStore} from "pinia";
import {kapitelDateString} from "@/graphql/kapitelTypes";
import {fetchPlanningStatus} from "@/helper/autopilotStatus";
import {generateCalendarMonth} from "@/helper/calendar/calendarMonth";
import {useGlobalEmitter} from "@/helper/emitter";
import {graphql} from "@/graphql/generated";
import {setRequestWithdrawalToSeenByWithdrawer} from "@/helper/bookingWithdrawalRequest";
import {consoleErrorApp} from "@/helper/console";
import date from "@/helper/datetime/date";

graphql(`
    fragment Objective on AInesObjective {
        reference
        subject
        state
        needsAction
        clickAction {
            type
            text
            richResponse
            assistantType
            richResponseInitData
        }
        title
        text
        icon
    }
`)

export async function fetchAInesObjectives(employeeId? : number): Promise<Array<AInesObjective>> {
    let result;
    if(!employeeId) {
        result = await query(
            graphql(`query getCurrentAInesObjectives {
                currentAInesObjectives {
                    ...Objective
                }
            }`)
        );
    }else {
        result = await query(
            graphql(`query getCurrentAInesObjectivesEmployee 
            (
                $employeeId: Int
            ){
                currentAInesObjectives(employeeId: $employeeId) {
                   ...Objective 
                }
            }`),
            {
                employeeId: employeeId,
            }
        )
    }

    return result.data?.currentAInesObjectives || [];

}


export async function handleObjectiveClick(objective: AInesObjective): Promise<void> {

    if(objective.clickAction.type === 'customFrontendHandling') {

        if (objective.subject === AInesObjectiveSubject.BookingWithdrawalRequest) {
            try {
                const refObj = JSON.parse(objective.reference)
                if (refObj?.requestId) {
                    if(objective.needsAction) {
                        const richResponseInitData = objective.clickAction.richResponseInitData ?? objective.reference
                        setRequestWithdrawalToSeenByWithdrawer(refObj.requestId)
                        presentScriptedMessage({
                            textContent: objective.clickAction.text,
                            richContents: objective.clickAction.richResponse ? [{
                                    type: objective.clickAction.richResponse ,
                                    initData: richResponseInitData
                                }]
                                : undefined
                        })
                    }else{
                        sendUserMessage(
                            objective.clickAction.text,
                            {
                                messageExpertAssistant:AInesAssistantType.RosterModify,
                                isScriptedContent: true
                            }
                        )
                    }
                }
            } catch (e) {
                consoleErrorApp('Could not parse objective reference for withdrawal request')
            }
        }

    } else
    if(objective.clickAction.type === 'userMessage') {
        const richResponseInitData = objective.clickAction.richResponseInitData ?? objective.reference
        await setUpAssistant()
        await sendUserMessage(
            objective.clickAction.text,
            {
                messageExpertAssistant: objective.clickAction.assistantType,
                isScriptedContent: true,
                richContentsPreview: objective.clickAction.richResponse ? [{
                    type: objective.clickAction.richResponse ,
                    initData: richResponseInitData
                }]
                    : undefined
            }
        )
    }else if(objective.clickAction.type === 'assistantMessage') {
        const richResponseInitData = objective.clickAction.richResponseInitData ?? objective.reference
        presentScriptedMessage({
            textContent: objective.clickAction.text,
            richContents: objective.clickAction.richResponse ? [{
                    type: objective.clickAction.richResponse ,
                    initData: richResponseInitData
                }]
                : undefined
        })
    }
}

// export enum AInesObjectiveUIState {
//     MINIMIZED = 'MINIMIZED',
//     VISIBLE = 'VISIBLE',
// }

const useAInesObjectiveStore_ = defineStore("AInesObjectives", {
    state: (): {
        loading: boolean,
        objectives: Array<AInesObjective>,
        selection: AInesObjective | undefined,
        // uiState: AInesObjectiveUIState | undefined,
        planningObjectivesProgress: Map<string, number>,
    } => ({
        loading: false,
        objectives: [],
        selection: undefined,
        // uiState: undefined,
        planningObjectivesProgress: new Map<string, number>(),
    }),
    getters: {
        objectivesLength: (state) => state.objectives.filter(o => o.needsAction).length,
        unhandledObjectivesLength: (state) => state.objectives.filter(o => o.needsAction).filter(o => o !== state.selection).length
    },
    actions: {
        async refetch() {
            this.loading = true
            this.objectives = []
            this.objectives = await fetchAInesObjectives()
            this.refetchPlanningObjectivesProgress()
            this.loading = false
        },
        async refetchPlanningObjectivesProgress() {
            this.planningObjectivesProgress = new Map<kapitelDateString, number>()
            // console.log('refetch planning objectives progress')
            for (const objective of this.objectives.filter(o =>
                o.state === AInesObjectiveState.InProgress
                && o.subject === AInesObjectiveSubject.Planning
            )) {

                fetchPlanningStatus(generateCalendarMonth(
                    objective.reference as kapitelDateString
                )).then((ps) => {
                    if (!ps) {
                        this.planningObjectivesProgress.set(objective.reference, 0)
                        return
                    }
                    this.planningObjectivesProgress.set(
                        objective.reference,
                        (
                            ps.bookings
                            / Math.max(
                                1,
                                ps.bookings + (ps.autopilotStatus?.missingBookings || 0)
                            )
                        )
                    )
                })
            }
        },
        // toggleUIState() {
        //     this.setUIState(this.uiState !== AInesObjectiveUIState.MINIMIZED ? AInesObjectiveUIState.MINIMIZED : AInesObjectiveUIState.VISIBLE)
        // },
        // setUIState(uiState: AInesObjectiveUIState) {
        //     this.uiState = uiState
        // },
        selectObjective(objective: AInesObjective | undefined) {
            this.selection = objective
        },
        deselectObjective() {
            this.selection = undefined
        }
    }
});
let AInesObjectiveStoreInit = false
export const useAInesObjectiveStore = () => {
    const store = useAInesObjectiveStore_()
    if (!AInesObjectiveStoreInit) {
        AInesObjectiveStoreInit = true

        useGlobalEmitter().on("AppStateChanged", ({state, impersonationUserSwitch}) => {
            if (state === 'employee' && impersonationUserSwitch) {
                store.refetch()
            }
        })

        useGlobalEmitter().on('objective:dirty', (subject: AInesObjectiveSubject) => {
            store.refetch()
        })

    }
    return store
}

export const needsAction = (objective: AInesObjective) => objective.needsAction


export enum ObjectiveGroupTitle {
    direktanfragen = 'Direktanfragen',
    zeiterfassung = 'Zeiterfassung',
    abrechnung = 'Abrechnung',
    erste = 'Erste Schritte',
    dienstplanung = 'Dienstplanung',
    diensttausch = 'Diensttausch',
    eingetragene = 'Eingetragene Dienste',
    anfragen = 'Anfragen ans IBFG Team'
}

export type ObjectiveGroup = { subject: AInesObjectiveSubject, title: ObjectiveGroupTitle, objectives: AInesObjective[] }

enum GroupPrio {
    urgent,
    todo_high,
    todo_low,
    status_high,
    status_low
}

const groupTitleOrderIndex = {
    [ObjectiveGroupTitle.direktanfragen]: GroupPrio.urgent,
    [ObjectiveGroupTitle.zeiterfassung]: GroupPrio.todo_high,
    [ObjectiveGroupTitle.abrechnung]: (group: ObjectiveGroup) => group.objectives.some(needsAction) ? GroupPrio.urgent : GroupPrio.status_low,
    [ObjectiveGroupTitle.erste]: GroupPrio.todo_low,
    [ObjectiveGroupTitle.dienstplanung]: (group: ObjectiveGroup) => group.objectives.some(needsAction) ? GroupPrio.todo_high : GroupPrio.status_high,
    [ObjectiveGroupTitle.diensttausch]: (group: ObjectiveGroup) => group.objectives.some(needsAction) ? GroupPrio.urgent : GroupPrio.status_high,
    [ObjectiveGroupTitle.eingetragene]: GroupPrio.todo_low,
    [ObjectiveGroupTitle.anfragen]: (group: ObjectiveGroup) => group.objectives.some(needsAction) ? GroupPrio.todo_low : GroupPrio.status_low,
} as {[key in ObjectiveGroupTitle] : (number | ((group:ObjectiveGroup) => number))}

export const objectiveSubjectToGroupTitleMap = {
    [AInesObjectiveSubject.Payroll]: ObjectiveGroupTitle.abrechnung,
    [AInesObjectiveSubject.Planning]: ObjectiveGroupTitle.dienstplanung,
    [AInesObjectiveSubject.Timesheet]: ObjectiveGroupTitle.zeiterfassung,
    [AInesObjectiveSubject.DirectDemand]: ObjectiveGroupTitle.direktanfragen,
    [AInesObjectiveSubject.RegisterBooking]: ObjectiveGroupTitle.eingetragene,
    [AInesObjectiveSubject.BookingSuggestion]: ObjectiveGroupTitle.diensttausch,
    [AInesObjectiveSubject.BookingWithdrawalRequest]: ObjectiveGroupTitle.diensttausch,
    [AInesObjectiveSubject.CandidateDemand]: ObjectiveGroupTitle.erste,
    [AInesObjectiveSubject.CandidateJobsites]: ObjectiveGroupTitle.erste,
    [AInesObjectiveSubject.OnboardingConfirmShiftTimes]: ObjectiveGroupTitle.erste,
    [AInesObjectiveSubject.OnboardingInitializeJobsites]: ObjectiveGroupTitle.erste,
    [AInesObjectiveSubject.OnboardingStartPlanning]: ObjectiveGroupTitle.erste,
    [AInesObjectiveSubject.HumanRequest]: ObjectiveGroupTitle.anfragen,
    [AInesObjectiveSubject.OnboardingSedcard]: ObjectiveGroupTitle.erste
} as {[key in AInesObjectiveSubject] : ObjectiveGroupTitle}

export const sortObjectiveGroups = (groups: ObjectiveGroup[]) => {
    const getTitleIndex = (group: ObjectiveGroup) => {
        const idx = groupTitleOrderIndex[group.title] // as (number | (group:Group) => number);
        return (typeof idx === "number") ? idx : idx(group)
    }
    groups.sort((g1, g2) => getTitleIndex(g1) < getTitleIndex(g2) ? -1 : 1)
}

export const sortObjectiveGroupContentDependingOnSubject = (group: ObjectiveGroup) => {
    switch (group.subject) {
        case AInesObjectiveSubject.Planning:
            return {
                ...group,
                objectives: group.objectives.sort((a, b) => date.cmpKapitelDate(a.reference as kapitelDateString, b.reference as kapitelDateString))
            }
        case AInesObjectiveSubject.Timesheet:
            return {
                ...group,
                objectives: group.objectives.sort((a, b) => date.cmpKapitelDate(
                        a.reference as kapitelDateString,
                        b.reference as kapitelDateString
                    )
                )
            }
        case AInesObjectiveSubject.BookingSuggestion:
        case AInesObjectiveSubject.BookingWithdrawalRequest:
            return {
                ...group,
                objectives: group.objectives.sort((a, b) => a.reference.localeCompare(b.reference))
            }
        case AInesObjectiveSubject.RegisterBooking:
            return {
                ...group,
                objectives: group.objectives.sort((a, b) => date.cmpKapitelDate(
                        a.reference as kapitelDateString,
                        b.reference as kapitelDateString
                    )
                )
            }
        // todo: dringende nach oben!
        // case AInesObjectiveSubject.HumanSupport:
        //     return {
        //         ...group,
        //         objectives: group.objectives.sort((a, b) => a.needsAction ? -1 : 1)
        //     }
        default:
            return group
    }
}
